import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { profile } from '@/data/profile'

const AnimatedBackground = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Grid pattern */}
    <div className="absolute inset-0 bg-[linear-gradient(rgba(27,59,122,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(27,59,122,0.03)_1px,transparent_1px)] bg-[size:50px_50px] dark:bg-[linear-gradient(rgba(167,194,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(167,194,255,0.03)_1px,transparent_1px)]" />
    
    {/* Gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5" />
    
    {/* Floating elements */}
    <motion.div
      animate={{
        y: [0, -20, 0],
        rotate: [0, 5, 0],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="absolute top-20 left-10 w-20 h-20 bg-accent/10 rounded-full blur-xl"
    />
    <motion.div
      animate={{
        y: [0, 20, 0],
        rotate: [0, -5, 0],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2,
      }}
      className="absolute top-40 right-20 w-32 h-32 bg-accent-contrast/10 rounded-full blur-xl"
    />
    <motion.div
      animate={{
        y: [0, -15, 0],
        rotate: [0, 3, 0],
      }}
      transition={{
        duration: 7,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 4,
      }}
      className="absolute bottom-20 left-1/4 w-16 h-16 bg-accent/15 rounded-full blur-lg"
    />
  </div>
)

export function Hero() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <AnimatedBackground />
      
      <div className="relative z-10 container-max section-padding text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Greeting */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-sm font-medium text-accent mb-4"
          >
            Hello, I'm
          </motion.p>

          {/* Name */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-light dark:text-text-dark mb-6"
          >
            <span className="gradient-text">{profile.name}</span>
          </motion.h1>

          {/* Role */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-xl sm:text-2xl lg:text-3xl font-medium text-text-light/80 dark:text-text-dark/80 mb-8"
          >
            {profile.role}
          </motion.h2>

          {/* Value proposition */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-lg text-text-light/70 dark:text-text-dark/70 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            {profile.bio}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              size="lg"
              onClick={() => scrollToSection('projects')}
              className="group"
            >
              View my work
              <motion.svg
                className="ml-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </motion.svg>
            </Button>
            
            <Button
              variant="secondary"
              size="lg"
              onClick={() => scrollToSection('contact')}
            >
              Get in touch
            </Button>
          </motion.div>

          {/* Social links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex justify-center space-x-6 mt-12"
          >
            {[
              { name: 'GitHub', href: profile.github },
              { name: 'LinkedIn', href: profile.linkedin },
              { name: 'Email', href: `mailto:${profile.email}` },
            ].map((link, index) => (
              <motion.a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-light/60 dark:text-text-dark/60 hover:text-accent transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
              >
                {link.name}
              </motion.a>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-text-light/30 dark:border-text-dark/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-text-light/30 dark:bg-text-dark/30 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}
